/**
 * Imperative caching of completed books for offline re-reading.
 *
 * When a book finishes generating (`book_complete` SSE event) we add its text
 * JSON and every page image to a runtime cache via the Cache Storage API. Book
 * content URLs are versioned in the path (e.g. `/books/<id>/v2/...`) so a
 * regenerated book is a brand-new cache entry rather than a stale overwrite —
 * see ADR-001 in the implementation plan.
 *
 * The cache name carries the book version so old versions can be evicted
 * precisely once a newer one is confirmed cached.
 */

import type { Book } from "@/lib/api";

const CACHE_PREFIX = "book-content-v1";

function cacheNameFor(version: number): string {
  return `${CACHE_PREFIX}:v${version}`;
}

function isSupported(): boolean {
  return typeof caches !== "undefined";
}

/** Collect the URLs that make a book fully available offline. */
function urlsForBook(book: Book): string[] {
  const urls = [book.contentUrl];
  if (book.coverUrl) urls.push(book.coverUrl);
  for (const page of book.pages) {
    if (page.imageUrl) urls.push(page.imageUrl);
  }
  // De-duplicate while preserving order.
  return Array.from(new Set(urls));
}

/**
 * Precache an entire completed book. Resolves to the number of assets cached;
 * resolves to 0 (without throwing) when the Cache API is unavailable so the
 * online experience is never blocked by an offline-only feature.
 */
export async function cacheBook(book: Book): Promise<number> {
  if (!isSupported()) return 0;
  const urls = urlsForBook(book);
  const cache = await caches.open(cacheNameFor(book.version));
  // addAll is atomic — if any asset 404s the whole call rejects, so add
  // resiliently one-by-one and tolerate individual misses.
  let cached = 0;
  await Promise.all(
    urls.map(async (url) => {
      try {
        const response = await fetch(url, { credentials: "include" });
        if (response.ok) {
          await cache.put(url, response.clone());
          cached += 1;
        }
      } catch {
        // A single missing asset must not fail the whole book cache.
      }
    }),
  );
  await evictOldVersions(book.version);
  return cached;
}

/**
 * Delete cached entries for older versions of any book once a newer version is
 * present. Keeps cache storage bounded after regenerations.
 */
async function evictOldVersions(currentVersion: number): Promise<void> {
  if (!isSupported()) return;
  const keys = await caches.keys();
  await Promise.all(
    keys
      .filter((key) => key.startsWith(`${CACHE_PREFIX}:v`))
      .filter((key) => {
        const v = Number.parseInt(key.split(":v")[1] ?? "", 10);
        return Number.isFinite(v) && v < currentVersion;
      })
      .map((key) => caches.delete(key)),
  );
}

/** True when a book's content JSON is already cached for offline reading. */
export async function isBookCached(book: BookCacheKey): Promise<boolean> {
  if (!isSupported()) return false;
  const cache = await caches.open(cacheNameFor(book.version));
  const match = await cache.match(book.contentUrl);
  return Boolean(match);
}

export interface BookCacheKey {
  version: number;
  contentUrl: string;
}
